import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Backdrop,
} from "@mui/material";
import {
  Home as HomeIcon,
  ShoppingCart as OrdersIcon,
  People as UsersIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  People as PeopleIcon,
  Terminal as TerminalIcon,
} from "@mui/icons-material";
import { motion } from "motion/react";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import { useLocation, useNavigate } from "react-router-dom";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import InventoryIcon from "@mui/icons-material/Inventory";
import BusinessIcon from "@mui/icons-material/Business";
import useMenuHook from "../hooks/useMenuHook";
import type { MenuItem } from "../types/menu.types";

const isMenuItemActive = (item: MenuItem, pathname: string) => {
  if (item.subNav?.kind === "tabs") {
    const { basePath } = item.subNav;
    return pathname === basePath || pathname.startsWith(`${basePath}/`);
  }
  if (item.subNav?.kind === "subpages") {
    if (pathname === item.path) return true;
    return item.subNav.routes.some(
      (r) => pathname === r.path || pathname.startsWith(`${r.path}/`),
    );
  }
  return pathname === item.path;
};

const SideMenu = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 700px)");
  const { menu } = useMenuHook();

  const [isOpen, setIsOpen] = useState(!isMobile);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Close mobile menu when screen size changes
  useEffect(() => {
    if (!isMobile) {
      setShowMobileMenu(false);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [isMobile]);

  const handleToggle = () => {
    if (isMobile) {
      setShowMobileMenu(!showMobileMenu);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleMenuItemClick = () => {
    if (isMobile) {
      setShowMobileMenu(false);
    }
  };

  return (
    <>
      {/* Mobile Hamburger Button */}
      {isMobile && !showMobileMenu && (
        <Box
          sx={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 1301,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <IconButton onClick={handleToggle} color="primary">
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {/* Mobile Backdrop */}
      {isMobile && showMobileMenu && (
        <Backdrop
          open={showMobileMenu}
          onClick={handleToggle}
          sx={{
            zIndex: 1299,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        />
      )}

      {/* Sidebar */}
      <motion.div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.paper,
          width: isMobile ? 280 : isOpen ? 280 : 80,
          position: isMobile ? "fixed" : "relative",
          top: 0,
          left: 0,
          zIndex: 1300,
          boxShadow:
            isMobile && showMobileMenu ? "0 4px 8px rgba(0,0,0,0.12)" : "none",
        }}
        animate={{
          width: isMobile ? 280 : isOpen ? 280 : 80,
          x: isMobile ? (showMobileMenu ? 0 : -280) : 0,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* Logo/Header */}
        <Box
          sx={{
            p: 3,
            position: "relative",
          }}
        >
          <motion.div
            animate={{ opacity: isMobile ? 1 : isOpen ? 1 : 0 }}
            transition={{
              duration: isMobile ? 0.3 : isOpen ? 1.5 : 0.2,
              ease: isMobile
                ? "easeInOut"
                : isOpen
                  ? [0.4, 0, 0.2, 1]
                  : [0.4, 0, 0.6, 1],
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold">
              Sistema Pedidos
            </Typography>
          </motion.div>
          {/* Desktop toggle button */}
          {!isMobile && (
            <Box sx={{ position: "absolute", right: -10, top: 25 }}>
              {isOpen ? (
                <ArrowCircleLeftIcon
                  sx={{
                    color: theme.palette.secondary.dark,
                    cursor: "pointer",
                  }}
                  onClick={handleToggle}
                />
              ) : (
                <ArrowCircleRightIcon
                  sx={{
                    color: theme.palette.secondary.dark,
                    cursor: "pointer",
                  }}
                  onClick={handleToggle}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Navigation Menu */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <List>
            {menu.map((item, index) => (
              <motion.div
                key={item.text}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => {
                  navigate(item.path);
                  handleMenuItemClick();
                }}
              >
                <ListItem disablePadding>
                  <ListItemButton
                    selected={isMenuItemActive(item, location.pathname)}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.light + "20",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                      {item.icon}
                    </ListItemIcon>
                    <motion.div
                      animate={{ opacity: isMobile ? 1 : isOpen ? 1 : 0 }}
                      transition={{
                        duration: isMobile ? 0.3 : isOpen ? 1.5 : 0.2,
                        ease: isMobile
                          ? "easeInOut"
                          : isOpen
                            ? [0.4, 0, 0.2, 1]
                            : [0.4, 0, 0.6, 1],
                      }}
                    >
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: 500,
                        }}
                      />
                    </motion.div>
                  </ListItemButton>
                </ListItem>
              </motion.div>
            ))}
          </List>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="caption" color="text.secondary" align="center">
            v1.0.0
          </Typography>
        </Box>
      </motion.div>
    </>
  );
};

export default SideMenu;
